import { Container, Graphics, Rectangle, Text, TextMetrics, TextStyle } from "pixi.js"

import { BasicSprite } from "../../sprites/basic_sprite"
import { HELP_ENTRIES } from "../help_entries"
import { BuilderPage, PageId } from "./page"
import { ACCENT, BODY_H, BODY_Y, CONTENT_W, RIM, TEXT, TEXT_DIM, CARD_FILL, CARD_RIM } from "./theme"
import { drawGlassPanel, sectionHeading, text } from "./widgets"

// The pages either side of the forge: the exile that puts you on the road, the briefing that sends
// you off, and the controls reference.

const LINE_H = 20
const TYPE_MS_PER_CHAR = 16

// A block of prose that types itself out. Pre-wrapped with TextMetrics so revealing a character at a
// time can't reflow the paragraph underneath itself (the usual word-wrap typewriter jitter); a click
// anywhere on the page finishes it immediately.
class Typewriter extends Container {
    private lines: Text[] = []
    private total = 0
    private revealed = 0
    private elapsed = 0
    private finished = false

    constructor(body: string, width: number, { size = 13, color = TEXT }: { size?: number; color?: number } = {}) {
        super()
        const style = new TextStyle({
            fontFamily: "Verdana, Geneva, sans-serif",
            fontSize: size,
            wordWrap: true,
            wordWrapWidth: width,
            strokeThickness: 1,
        })
        const measured = TextMetrics.measureText(body, style)
        measured.lines.forEach((line, i) => {
            const label = text("", { size, color })
            label.y = i * LINE_H
            this.addChild(label)
            this.lines.push(label)
            this.total += line.length
            // Stash the finished line on the object so the reveal doesn't have to re-measure.
            ;(label as any)._full = line
        })
    }

    finish() {
        this.finished = true
        this.lines.forEach(line => (line.text = (line as any)._full))
    }

    animate(dtMs: number) {
        if (this.finished) {
            return
        }
        this.elapsed += dtMs
        const target = Math.floor(this.elapsed / TYPE_MS_PER_CHAR)
        if (target <= this.revealed) {
            return
        }
        this.revealed = Math.min(target, this.total)
        let remaining = this.revealed
        for (let i = 0; i < this.lines.length; i++) {
            const full: string = (this.lines[i] as any)._full
            const take = Math.max(0, Math.min(full.length, remaining))
            if (this.lines[i].text.length !== take) {
                this.lines[i].text = full.slice(0, take)
            }
            remaining -= full.length
        }
        if (this.revealed >= this.total) {
            this.finished = true
        }
    }
}

export class IntroPage extends BuilderPage {
    readonly pageId: PageId = "intro"
    readonly title = "The long exile"

    private typewriter?: Typewriter

    animate(dtMs: number) {
        this.typewriter?.animate(dtMs)
    }

    build() {
        this.wipe()
        this.typewriter = undefined

        const panelW = 560
        const panelX = (CONTENT_W - panelW) / 2
        const panelH = 190
        const panelY = BODY_Y + (BODY_H - panelH) / 2

        const panel = new Graphics()
        drawGlassPanel(panel, panelX, panelY, panelW, panelH, { radius: 12 })
        this.addChild(panel)

        const rule = new Graphics()
        rule.beginFill(RIM, 0.8)
        rule.drawRect(panelX + 28, panelY + 34, 44, 2)
        rule.endFill()
        this.addChild(rule)

        const kicker = text("BY ORDER OF THE EMPEROR", { size: 10, color: ACCENT, bold: true })
        kicker.x = panelX + 28
        kicker.y = panelY + 18
        kicker.alpha = 0.9
        this.addChild(kicker)

        const body = new Typewriter(
            "You have been exiled to the barbarous lands of the West, for the Emperor will suffer no rivals in the East.",
            panelW - 56,
            { size: 14 },
        )
        body.x = panelX + 28
        body.y = panelY + 54
        this.addChild(body)
        this.typewriter = body

        const footnote = text("Click to skip ahead", { size: 9, color: TEXT_DIM })
        footnote.x = panelX + 28
        footnote.y = panelY + panelH - 26
        footnote.alpha = 0.7
        this.addChild(footnote)

        // The whole body is the skip target -- no hunting for a button. A Graphics with no geometry
        // of its own can't be hit-tested, so the page carries an explicit hit area.
        this.interactive = true
        this.hitArea = new Rectangle(0, BODY_Y, CONTENT_W, BODY_H)
        this.cursor = "pointer"
        this.on("pointerdown", () => this.typewriter?.finish())

        this.host.setNavButtons([
            {
                title: "Forge your character ▸",
                primary: true,
                callback: () => this.host.switchPage("build"),
            },
        ])
    }
}

const STEPS = [
    "Gather firewood. Wait for night time, and create a campfire.",
    "Stay within that fire's light, and recruit the mercenaries that come.",
    "Seek your enemies' strongholds, and defeat them.",
]

export class JourneyPage extends BuilderPage {
    readonly pageId: PageId = "journey"
    readonly title = "Journey onward"

    build() {
        this.wipe()

        const panelW = 470
        const panelX = 24
        const panel = new Graphics()
        drawGlassPanel(panel, panelX, BODY_Y, panelW, BODY_H, { radius: 12 })
        this.addChild(panel)

        const lead = text("Beware of the local warlords! Raise your forces and conquer this savage land.", {
            size: 12,
            color: TEXT,
        })
        lead.x = panelX + 22
        lead.y = BODY_Y + 18
        this.addChild(lead)

        const heading = sectionHeading("Your first nights", panelW - 44)
        heading.x = panelX + 22
        heading.y = BODY_Y + 48
        this.addChild(heading)

        let y = BODY_Y + 70
        STEPS.forEach((step, i) => {
            const card = new Graphics()
            card.lineStyle(1, CARD_RIM, 0.6)
            card.beginFill(CARD_FILL, 0.85)
            card.drawRoundedRect(panelX + 22, y, panelW - 44, 48, 8)
            card.endFill()
            card.lineStyle(0)
            // Numbered badge.
            card.beginFill(RIM, 0.9)
            card.drawCircle(panelX + 46, y + 24, 12)
            card.endFill()
            this.addChild(card)

            const number = text(`${i + 1}`, { size: 12, color: 0x2a1c0c, bold: true })
            number.x = panelX + 46 - number.width / 2
            number.y = y + 24 - number.height / 2
            this.addChild(number)

            const body = text(step, { size: 11, color: TEXT })
            body.x = panelX + 68
            body.y = y + 24 - body.height / 2
            this.addChild(body)

            y += 56
        })

        const closer = text("Explore this land, and harden yourself through its perils!", {
            size: 11,
            color: ACCENT,
        })
        closer.x = panelX + 22
        closer.y = BODY_Y + BODY_H - 30
        this.addChild(closer)

        this.drawCampfire(panelX + panelW + 90, BODY_Y + BODY_H / 2)
    }

    // The campfire the briefing is about, burning beside it.
    private drawCampfire(x: number, y: number) {
        const halo = new Graphics()
        halo.beginFill(0xff8a24, 0.08)
        halo.drawCircle(x, y, 96)
        halo.endFill()
        halo.beginFill(0xffcf5a, 0.07)
        halo.drawCircle(x, y, 58)
        halo.endFill()
        this.addChild(halo)

        const campfire = new BasicSprite({ name: "lpc-terrain:760", scale: 1.0, color: 0xffffff })
        const fire = new BasicSprite({ name: "fire", scale: 1.0, color: 0xffffff })
        fire.x = 3
        fire.y = -23

        const animation = new Graphics()
        animation.addChild(campfire)
        animation.addChild(fire)
        animation.scale.set(3.0)
        animation.x = x
        animation.y = y
        this.addChild(animation)
    }
}

export class ControlsPage extends BuilderPage {
    readonly pageId: PageId = "controls"
    readonly title = "Controls"

    build() {
        this.wipe()

        const panelW = 640
        const panelX = (CONTENT_W - panelW) / 2
        const panel = new Graphics()
        drawGlassPanel(panel, panelX, BODY_Y, panelW, BODY_H, { radius: 12 })
        this.addChild(panel)

        // Two columns of entries, so nothing runs off the bottom of the panel.
        const colW = (panelW - 66) / 2
        const rows = Math.ceil(HELP_ENTRIES.length / 2)
        HELP_ENTRIES.forEach((entry, i) => {
            const col = Math.floor(i / rows)
            const row = i % rows
            const x = panelX + 24 + col * (colW + 18)
            const y = BODY_Y + 20 + row * 62

            const title = text(entry.title, { size: 11, color: ACCENT, bold: true })
            title.x = x
            title.y = y
            this.addChild(title)

            const body = text(entry.body, { size: 10, color: TEXT, wrap: colW })
            body.x = x
            body.y = y + 16
            this.addChild(body)
        })

        this.host.setNavButtons([
            {
                title: "◂ Back",
                callback: () => {
                    this.host.resetNavButtons()
                    this.host.switchPage("journey")
                },
            },
        ])
    }
}
